import { differenceInYears, format, parse } from 'date-fns'

import {
  FOLLOWUP_SESSION_TYPES,
  INTAKE_SESSION_TYPES,
  LTSessionType,
  LTSessionTypes,
  SessionCostEstimateDisplayState,
} from './constants'
import { ClientObject } from '../../models/client/Client'
import { FeedbackResponse } from '../../models/feedback/Feedback'
import { LTProgramName, ProgramNames } from '../../models/programs/Programs'
import { CostInfo } from '../../models/providers/Providers'
import { MEETING_FORMATS } from '../../models/session/Session'
import { EAPAndHPIProgramSupport, ProgramCoverageBreakdown, YearProgramCoverage } from '../../models/user/User'
import { FORCE_ADULT_FLOWS_TO_HPI_CUSTOMERS } from '../common/constants/healthPlanAndPaymentInformationConstants'
import { assertUnreachable } from '../common/utils/commonUtils'
import { convertDateStringFromISOToUSFormat } from '../common/utils/dateUtils'
import { Programs, PROGRAMS } from '../programConfigs/programConfigs'
import { isMinor } from '../searchForCare/utils'

export const translateFeedbackResponse = (response: { [key: string]: any }): FeedbackResponse[] =>
  Object.entries(response)
    .filter(([, value]) => value !== undefined)
    .map(([key, value]) => ({ question: key, answer: value.toString() }))

export const programSupportForAttribute = (
  attribute: keyof EAPAndHPIProgramSupport,
  selectedEpisode?: {
    program_name?: string
  },
  programCoverageBreakdown?: ProgramCoverageBreakdown,
) => {
  const currentProgramCoverageBreakdown =
    programCoverageBreakdown && programCoverageBreakdown[format(new Date(), 'yyyy')]

  const customerPropertyId = selectedEpisode?.program_name
    ? PROGRAMS[selectedEpisode.program_name as ProgramNames]?.customerPropertyId
    : undefined
  const validProgramCoverage =
    customerPropertyId && currentProgramCoverageBreakdown && customerPropertyId in currentProgramCoverageBreakdown

  return (
    attribute &&
    validProgramCoverage &&
    currentProgramCoverageBreakdown[customerPropertyId as keyof YearProgramCoverage]?.[attribute]
  )
}

/**
 * Determines if EAP session limit information should be shown for a client
 *
 * EAP session limit info should not be shown for adults when forceAdultFlowsToHPI is true, the client's company is Amazon, and the program is not coaching for parents
 *
 * @param forceAdultFlowsToHPI - LD flag
 * @param clientEmployer - Client's employer
 * @param clientDateOfBirth - Client's date of birth
 * @param programName - Program name
 * @returns True if EAP session limit information should be shown, false otherwise
 */
export const shouldShowEAPSessionLimitInfo = (
  forceAdultFlowsToHPI: boolean,
  clientEmployer?: string,
  clientDateOfBirth?: string,
  programName?: string,
): boolean => {
  const isClientMinor = clientDateOfBirth ? isMinor(convertDateStringFromISOToUSFormat(clientDateOfBirth) ?? '') : false

  return !(
    forceAdultFlowsToHPI &&
    FORCE_ADULT_FLOWS_TO_HPI_CUSTOMERS.includes(clientEmployer ?? '') &&
    !isClientMinor &&
    programName !== ProgramNames.CoachingForParents
  )
}

/**
 * Resolves the appropriate session limit based on the program name and client details
 *
 * For CoachingForParents program, returns cfp_sessions_limit if the client's employer is in FORCE_ADULT_FLOWS_TO_HPI_CUSTOMERS and forceAdultFlowsToHPI is true
 * For all other programs, returns sessions_limit
 *
 * The CFP session limit is only used for Amazon.
 *
 * @param programName - Program name
 * @param clientDetails - Client details containing session limits
 * @param forceAdultFlowsToHPI - LD flag
 * @returns Session limit or CFP session limit if the customer is Amazon
 */
export const resolveSessionLimit = (
  programName?: string,
  clientDetails?: {
    cfp_sessions_limit?: number
    sessions_limit?: number
    eap_session_limit?: number
    employer?: string
  } | null,
  forceAdultFlowsToHPI: boolean = false,
  isEAP: boolean = false,
): number | undefined => {
  if (!clientDetails) {
    return undefined
  }

  if (
    forceAdultFlowsToHPI &&
    FORCE_ADULT_FLOWS_TO_HPI_CUSTOMERS.includes(clientDetails.employer ?? '') &&
    programName === ProgramNames.CoachingForParents
  ) {
    return clientDetails.cfp_sessions_limit
  }

  return isEAP ? clientDetails.eap_session_limit : clientDetails.sessions_limit
}

/**
 * For adult clients of some specific customers (Amazon), return a new program coverage object with EAP coverage removed for the following programs:
 *
 * considerLyraTherapy, directAccessTherapy
 *
 * These are programs that should have EAP enabled only for minor clients.
 *
 * For any other customer or minors, return the same customer program coverage object.
 *
 */
export function getModifiedProgramCoverageForBenefitsEligibility(
  forceAdultFlowsToHPI: boolean,
  currentProgramCoverageBreakdown: YearProgramCoverage,
  clientDetails?: Pick<ClientObject, 'date_of_birth' | 'employer'>,
): YearProgramCoverage {
  if (
    forceAdultFlowsToHPI &&
    clientDetails?.employer &&
    FORCE_ADULT_FLOWS_TO_HPI_CUSTOMERS.includes(clientDetails.employer) &&
    clientDetails?.date_of_birth &&
    !isMinor(convertDateStringFromISOToUSFormat<never>(clientDetails.date_of_birth)) &&
    (currentProgramCoverageBreakdown.considerLyraTherapy || currentProgramCoverageBreakdown.directAccessTherapy)
  ) {
    return {
      ...currentProgramCoverageBreakdown,
      ...(currentProgramCoverageBreakdown.considerLyraTherapy
        ? {
            considerLyraTherapy: {
              ...currentProgramCoverageBreakdown.considerLyraTherapy,
              eapSupported: false,
            },
          }
        : undefined),
      ...(currentProgramCoverageBreakdown.directAccessTherapy
        ? {
            directAccessTherapy: {
              ...currentProgramCoverageBreakdown.directAccessTherapy,
              eapSupported: false,
            },
          }
        : undefined),
    }
  }

  return currentProgramCoverageBreakdown
}

const INITIAL_DA_SESSION_TYPES = new Set<string>([
  LTSessionTypes.INITIAL_DA_SESSION,
  LTSessionTypes.INITIAL_DA_COUPLES_SESSION,
  LTSessionTypes.INITIAL_DA_FAMILY_SESSION,
  LTSessionTypes.INITIAL_DA_CHILD_SESSION,
])

/**
 * Returns whether sessionType matches the program's initial session type.
 * Returns undefined when the program config or session type is missing, or when the type is unrecognized
 * so callers can fall back to their existing logic.
 */
export const isInitialEvaluationSessionType = (
  sessionType: string | undefined,
  programName: string | undefined,
): boolean | undefined => {
  const sessionTypeConfig = programName ? Programs.getProgramConfigByName(programName)?.sessionTypeConfig : undefined
  if (!sessionType || !sessionTypeConfig) {
    return undefined
  }
  if (sessionType === sessionTypeConfig.sessionType || sessionType === sessionTypeConfig.minorSessionType) {
    return true
  }
  if (
    sessionType === sessionTypeConfig.followupSessionType ||
    sessionType === sessionTypeConfig.minorFollowupSessionType
  ) {
    return false
  }
  return undefined
}

type SessionKindFields = {
  sessionType?: string | null
  suggestedSessionType?: string | null
}

/**
 * Classifies a session as intake or follow-up from its billed sessionType, then the backend's suggestedSessionType
 * (set on retro appointments and transitioned legacy clients' first sessions, which bill as follow-ups).
 * Returns undefined when neither is a recognized intake/follow-up type.
 */
export const getSessionKindFromType = ({
  sessionType,
  suggestedSessionType,
}: SessionKindFields): 'intake' | 'followup' | undefined => {
  for (const type of [sessionType, suggestedSessionType]) {
    if (type && INTAKE_SESSION_TYPES.has(type)) {
      return 'intake'
    }
    if (type && FOLLOWUP_SESSION_TYPES.has(type)) {
      return 'followup'
    }
  }
  return undefined
}

// Falls back to sessionNumber when the type is unknown; DA appointments report a sessionNumber of 0.
export const isIntakeSession = (session: SessionKindFields & { sessionNumber?: number | null }): boolean => {
  const kind = getSessionKindFromType(session)
  return kind ? kind === 'intake' : session.sessionNumber != null && session.sessionNumber <= 1
}

const getValidBaseRate = (
  sessionType: string,
  sessionsRates: { [key: string]: string },
): { rate: string; parsedRate: number } => {
  const baseRate = sessionsRates[sessionType]
  const parsedBaseRate = parseFloat(baseRate)

  if (baseRate != null && !isNaN(parsedBaseRate) && parsedBaseRate > 0) {
    return { rate: baseRate, parsedRate: parsedBaseRate }
  }

  const isInitialSession = INITIAL_DA_SESSION_TYPES.has(sessionType)
  const fallbackSessionType = isInitialSession ? LTSessionTypes.INITIAL_DA_SESSION : LTSessionTypes.FOLLOWUP_DA_SESSION
  const fallbackRate = sessionsRates[fallbackSessionType]
  const parsedFallbackRate = parseFloat(fallbackRate)

  if (fallbackRate != null && !isNaN(parsedFallbackRate) && parsedFallbackRate > 0) {
    return { rate: fallbackRate, parsedRate: parsedFallbackRate }
  }

  return { rate: '0', parsedRate: 0 }
}

const calculateSessionRate = (
  sessionType: string,
  sessionsRates: { [key: string]: string },
  modality?: string,
  differentialPricingEnabled?: boolean,
  differentialPricingModifier?: number,
): string => {
  // NOTE: EndOfSession passes two different values for phone modality: phone and tele_therapy.
  // If we need to add logic that uses this modality we would have to use both these values - similar to how we handle in_person
  const { rate: baseRate, parsedRate: parsedBaseRate } = getValidBaseRate(sessionType, sessionsRates)
  const hasValidBaseRate = parsedBaseRate > 0

  if (
    differentialPricingEnabled &&
    (modality === MEETING_FORMATS.IN_PERSON || modality === MEETING_FORMATS.IN_PERSON_UNDERSCORE) &&
    differentialPricingModifier &&
    hasValidBaseRate
  ) {
    return (differentialPricingModifier + parsedBaseRate).toString()
  }

  return baseRate
}

export const isDateOfBirthUnder18 = (dateOfBirth: string) => {
  if (!dateOfBirth) {
    return false
  }

  try {
    // Determine the separator and format based on the input string
    const hasDash = dateOfBirth.includes('-')
    const hasSlash = dateOfBirth.includes('/')

    let birthDate: Date

    if (hasDash && !hasSlash) {
      // Parse the date string in YYYY-MM-DD format
      birthDate = parse(dateOfBirth, 'yyyy-MM-dd', new Date())
    } else if (hasSlash && !hasDash) {
      // Parse the date string in YYYY/MM/DD format
      birthDate = parse(dateOfBirth, 'yyyy/MM/dd', new Date())
    } else {
      // Invalid format or mixed separators
      return false
    }

    // Check if the parsed date is valid
    if (isNaN(birthDate.getTime())) {
      return false
    }

    const today = new Date()
    const age = differenceInYears(today, birthDate)

    return age < 18
  } catch {
    return false
  }
}

type SessionTypeConfig = NonNullable<ReturnType<typeof Programs.getProgramConfigByName>>['sessionTypeConfig']

/**
 * Remaps a backend-supplied `suggestedSessionType` to its minor (child) equivalent when the client is
 * under 18 and the program defines minor session types (currently only DirectAccessIndividualTherapy).
 *
 * The backend computes `suggestedSessionType` from `program.sessionTypeInitial`/`sessionTypeRecurring`,
 * which are age-unaware — for a minor on the adult program it returns the adult type (e.g.
 * `individual_follow_up_session`) when the billable type should be the child one
 * (`child_follow_up_session`). We map the adult initial/follow-up values to their child counterparts.
 * Anything already in child form, or any value we don't recognize, passes through unchanged.
 */
const remapSuggestedSessionTypeForMinor = (
  suggestedSessionType: LTSessionType,
  config: NonNullable<SessionTypeConfig>,
  dateOfBirth?: string,
): string => {
  if (!config.minorSessionType || !dateOfBirth || !isDateOfBirthUnder18(dateOfBirth)) {
    return suggestedSessionType
  }
  if (suggestedSessionType === config.sessionType) {
    return config.minorSessionType
  }
  if (config.followupSessionType && suggestedSessionType === config.followupSessionType) {
    return config.minorFollowupSessionType ?? config.minorSessionType
  }
  return suggestedSessionType
}

export const getLTSessionTypeAndRate = ({
  programName,
  sessionNumber,
  sessionsRates = {},
  modality,
  differentialPricingEnabled,
  differentialPricingModifier,
  dateOfBirth,
  isInternationalCoachingSessionTypeEnabled,
  clientCountry,
  bookedSessionType,
  suggestedSessionType,
}: {
  programName: LTProgramName
  sessionNumber: number
  sessionsRates: { [key: string]: string }
  modality?: string
  differentialPricingEnabled?: boolean
  differentialPricingModifier?: number
  dateOfBirth?: string
  isInternationalCoachingSessionTypeEnabled?: boolean
  clientCountry?: string
  bookedSessionType?: LTSessionType | null
  suggestedSessionType?: LTSessionType
}): { sessionType: string; sessionRate: string } => {
  const config = Programs.getProgramConfigByName(programName)?.sessionTypeConfig
  if (!config) {
    throw new Error('No sessionTypeConfig for program: ' + programName)
  }

  let sessionType: string
  // The international coaching override is orthogonal to initial-vs-follow-up: the backend's
  // suggestedSessionType encodes only the latter and is country-unaware, so the international rule
  // must take precedence for non-US coaching even on retro appointments. Only the Coaching program
  // defines internationalSessionType, so this branch never affects DA flows.
  if (config.internationalSessionType && isInternationalCoachingSessionTypeEnabled && clientCountry !== 'US') {
    sessionType = config.internationalSessionType
  } else if (bookedSessionType) {
    // The booked type records the session selected for this appointment and is authoritative.
    // Unlike the retroactive-appointment suggestion below, it is already age-aware and should not be remapped.
    sessionType = bookedSessionType
  } else if (suggestedSessionType) {
    // suggestedSessionType is backend-gated: only set on retroactive appointments where the backend
    // has determined initial-vs-follow-up. It is age-unaware, so we still layer the DOB->child remap
    // on top so a minor on the adult program is billed the child session type/rate.
    sessionType = remapSuggestedSessionTypeForMinor(suggestedSessionType, config, dateOfBirth)
  } else if (config.minorSessionType && dateOfBirth && isDateOfBirthUnder18(dateOfBirth)) {
    sessionType =
      sessionNumber > 1 ? (config.minorFollowupSessionType ?? config.minorSessionType) : config.minorSessionType
  } else {
    sessionType = sessionNumber > 1 ? (config.followupSessionType ?? config.sessionType) : config.sessionType
  }

  let sessionRate: string = '0'
  switch (config.rateStrategy) {
    case 'fixed':
      sessionRate = String(config.fixedRate ?? 0)
      break
    case 'lookupSessionRate':
      sessionRate = sessionsRates[sessionType] || '0'
      break
    case 'differentialPricingCalculation':
      sessionRate = calculateSessionRate(
        sessionType,
        sessionsRates,
        modality,
        differentialPricingEnabled,
        differentialPricingModifier,
      )
      break
    default:
      assertUnreachable(config.rateStrategy, 'Unknown rate strategy: ' + config.rateStrategy)
  }

  return { sessionType, sessionRate }
}

/**
 * Merges cost information and its display state from a subset of appointments back into the
 * full list of appointments. Used when costs are only fetched for appointments requiring HPI,
 * but need to be displayed alongside all appointments.
 *
 * @param allAppointments - Full list of appointments
 * @param appointmentsWithCosts - Subset of appointments that have cost information
 * @returns Full list of appointments with cost information merged in where available
 */
export const mergeAppointmentCosts = <T extends { appointmentId: number }>(
  allAppointments: T[],
  appointmentsWithCosts: Array<
    T & {
      costInfo?: CostInfo
      costEstimateDisplayState?: SessionCostEstimateDisplayState
    }
  >,
): Array<
  T & {
    costInfo?: CostInfo
    costEstimateDisplayState?: SessionCostEstimateDisplayState
  }
> => {
  const costDataById: Record<
    number,
    {
      costInfo?: CostInfo
      costEstimateDisplayState?: SessionCostEstimateDisplayState
    }
  > = {}
  appointmentsWithCosts.forEach((appt) => {
    costDataById[appt.appointmentId] = {
      costInfo: appt.costInfo,
      costEstimateDisplayState: appt.costEstimateDisplayState,
    }
  })

  return allAppointments.map((appointment) => ({
    ...appointment,
    ...costDataById[appointment.appointmentId],
  }))
}
